import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Button, Modal, ModalHeader, ModalBody,
  Form, Label, Input, Pagination, PaginationItem, PaginationLink, FormFeedback
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from 'react-toastify';
import moment from "moment";

// Import Components & Services
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
import { fetchLokasis } from '../../services/lokasiService';
import { fetchPemilikLahans } from '../../services/pemilikLahanService';
import { 
  fetchTransaksiPembelian, createTransaksiPembelian, 
  updateTransaksiPembelian, deleteTransaksiPembelian 
} from '../../services/transaksiPembelianService';


import { getUserLevel } from '../../helpers/auth'; 
const TransaksiPembelianTanah = () => {
  document.title = "Transaksi Pembelian Tanah | Manajemen Proyek";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [userLevel, setUserLevel] = useState(null);
  const [userDivisi, setUserDivisi] = useState(null);
  // Di dalam komponen TransaksiPembelianTanah
const user = JSON.parse(localStorage.getItem("authUser")); // sesuaikan key-nya


const isDirektur = userLevel === "Dirut" || userLevel === "dirut";

  // State Zoom Gambar
  const [imgModal, setImgModal] = useState(false);
  const [viewImage, setViewImage] = useState("");

  // Master Data
  const [optLokasi, setOptLokasi] = useState([]);
  const [optPemilik, setOptPemilik] = useState([]);
  
  // Pagination & Filter
  const [pagination, setPagination] = useState({ page: 1, total: 0, last_page: 1, per_page: 10 });
  const [filters, setFilters] = useState({ id_lokasi: "", id_pemilik: "" });

  // --- UTILS ---
  const formatIDR = (num) => {
    if (num === null || num === undefined || num === "") return "";
    return new Intl.NumberFormat('id-ID').format(num);
  };

  const parseNumber = (val) => {
    if (!val) return 0;
    const cleanValue = val.toString().replace(/\./g, "");
    return parseInt(cleanValue, 10) || 0;
  };

  // --- HANDLERS ---
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilter = () => {
    setFilters({ id_lokasi: "", id_pemilik: "" });
  };

    useEffect(() => {
      const authInfo = getUserLevel(); 
      
      if (authInfo && typeof authInfo === 'object') {
          const { level, divisi } = authInfo; 
  
          setUserLevel(level); 
          // Penting: Pastikan 'divisi' di sini adalah STRING Nama Divisi
          setUserDivisi(divisi); 
  
          // Console Log (untuk debugging)
          console.log("=================================================");
          console.log("Level Pengguna Aktif:", level);
          console.log("Nama Divisi Aktif (dari JWT):", divisi); 
          console.log("=================================================");
      } else {
          setUserLevel(null);
          setUserDivisi(null);
      }
      
    }, []);

  // --- FETCH DATA (Logic Pagination Utama) ---
  const fetchData = useCallback(async (pageNumber = 1) => {
    setLoading(true);
    try {
      const response = await fetchTransaksiPembelian({ 
        page: pageNumber, 
        per_page: pagination.per_page, 
        id_lokasi: filters.id_lokasi,
        id_pemilik: filters.id_pemilik
      });

      if (response && response.success) {
        setData(response.data || []);
        
        // Menyesuaikan dengan struktur response JSON Anda
        const meta = response.meta;
        setPagination({
          page: Number(meta.page) || pageNumber,
          total: Number(meta.total) || 0,
          per_page: Number(meta.per_page) || 10,
          // Hitung last_page: total 28 / per_page 10 = 2.8 -> ceil jadi 3 halaman
          last_page: Math.ceil(meta.total / meta.per_page) || 1
        });
      }
    } catch (e) {
      toast.error("Gagal mengambil data transaksi");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.per_page]);

  // Efek: Ambil data saat filter berubah (selalu balik ke hal 1)
  useEffect(() => {
    fetchData(1);
  }, [filters, fetchData]);

  // Load Master Data
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [resLokasi, resPemilik] = await Promise.all([
          fetchLokasis({ disable_pagination: true }),
          fetchPemilikLahans({ disable_pagination: true })
        ]);
        setOptLokasi(resLokasi.data || []);
        setOptPemilik(resPemilik.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    loadMasters();
  }, []);

  // --- PAGINATION GROUPING ---
  const getPaginationGroup = () => {
    let start = Math.max(pagination.page - 2, 1);
    let end = Math.min(start + 4, pagination.last_page);
    if (pagination.last_page > 5 && end === pagination.last_page) {
      start = Math.max(end - 4, 1);
    }
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // --- FORMIK ---
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      tgl_pembelian: selectedData?.tgl_pembelian ? moment(selectedData.tgl_pembelian).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      id_lokasi: selectedData?.id_lokasi || "",
      id_pemilik: selectedData?.id_pemilik || "",
      luas_tanah: selectedData?.luas_tanah || "",
      harga_beli_tanah: selectedData?.harga_beli_tanah || "",
      keterangan: selectedData?.keterangan || "",
      file_hasil: null
    },
   validationSchema: Yup.object({
  tgl_pembelian: Yup.string().required("Tanggal wajib diisi"),
  id_lokasi: Yup.string().required("Lokasi wajib dipilih"),
  id_pemilik: Yup.string().required("Pemilik wajib dipilih"),
  luas_tanah: Yup.number().typeError("Harus angka").min(1, "Minimal 1m²").required("Wajib diisi"),
  harga_beli_tanah: Yup.number().typeError("Harus angka").min(1, "Minimal Rp 1").required("Wajib diisi"),
  
  // Wajib jika: buat baru, ATAU edit tapi belum ada file sebelumnya
  file_hasil: Yup.mixed().when([], {
    is: () => !isEdit || !selectedData?.hasil_ukur_path,
    then: (schema) => schema.required("File hasil ukur wajib diupload"),
    otherwise: (schema) => schema.nullable(),
  }),
}),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("tgl_pembelian", values.tgl_pembelian);
      formData.append("id_lokasi", values.id_lokasi);
      formData.append("id_pemilik", values.id_pemilik);
      formData.append("luas_tanah", values.luas_tanah);
      formData.append("harga_beli_tanah", values.harga_beli_tanah);
      formData.append("keterangan", values.keterangan || "");
      
      if (values.file_hasil) formData.append("hasil_ukur_path", values.file_hasil);

      try {
        let res;
        if (isEdit) {
            formData.append("_method", "PUT");
            res = await updateTransaksiPembelian(selectedData.id_transaksi, formData);
        } else {
            res = await createTransaksiPembelian(formData);
        }
        
        if (res.success) {
          toast.success("Data berhasil disimpan");
          setModal(false);
          fetchData(pagination.page); // Stay di halaman yang sama
        }
      } catch (e) { 
        toast.error(e.response?.data?.message || "Gagal menyimpan"); 
      } finally {
        setIsSubmitting(false);
      }
    }
  });



  const columns = useMemo(() => {
  const baseColumns = [
       { 
      header: 'Dokumen', 
      cell: (info) => {
        const path = info.row.original.hasil_ukur_path;
        return path ? (
          <div className="text-center">
            {path.match(/\.(jpeg|jpg|png|webp|gif)$/i) ? (
              <img src={path} alt="Ukur" className="img-thumbnail" style={{ height: "35px", width: "35px", objectFit: "cover", cursor: "pointer" }} onClick={() => { setViewImage(path); setImgModal(true); }} />
            ) : (
              <a href={path} target="_blank" rel="noreferrer" className="btn btn-sm btn-soft-danger"><i className="mdi mdi-file-pdf-box font-size-16"></i></a>
            )}
          </div>
        ) : "-";
      }
    },
    { header: 'Tanggal', cell: (info) => moment(info.row.original.tgl_pembelian).format("DD/MM/YYYY") },
    { header: 'Kode', cell: (info) => <span className="fw-medium text-primary">{info.row.original.kode_transaksi}</span> },
    { header: 'Lokasi', cell: (info) => info.row.original.nama_lokasi || "-" },
    { header: 'Pemilik', cell: (info) => info.row.original.nama_pemilik || "-" },
    { header: 'Luas', cell: (info) => <span>{formatIDR(info.row.original.luas_tanah)} m²</span> },
    { header: 'Harga Beli', cell: (info) => <span className="fw-bold text-success">Rp {formatIDR(info.row.original.harga_beli_tanah)}</span> },
  ];

  // Tambahkan kolom Aksi HANYA jika dia direktur
  if (isDirektur) {
    baseColumns.push({
      header: 'Aksi',
      cell: (info) => (
        <div className="hstack gap-2">
          <Button size="sm" color="soft-info" onClick={() => { setSelectedData(info.row.original); setIsEdit(true); setModal(true); }}>
            <i className="mdi mdi-pencil" />
          </Button>
          <Button size="sm" color="soft-danger" onClick={() => { setSelectedData(info.row.original); setDeleteModal(true); }}>
            <i className="mdi mdi-delete" />
          </Button>
        </div>
      )
    });
  }

  return baseColumns;
}, [isDirektur]); // Pastikan isDirektur masuk ke dependency array

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Aset" breadcrumbItem="Pembelian Tanah" />
          
          <DeleteModal 
            show={deleteModal} 
            onDeleteClick={async () => { 
                await deleteTransaksiPembelian(selectedData.id_transaksi); 
                setDeleteModal(false); 
                fetchData(1); 
                toast.success("Berhasil dihapus");
            }} 
            onCloseClick={() => setDeleteModal(false)} 
          />
          {/* --- FILTER RESPONSIVE --- */}
<Card>
  <CardBody>
    <Form>
      <Row className="g-3"> {/* g-3 memberikan gap standar antar kolom */}
        
        {/* Filter Lokasi */}
        <Col xs={12} sm={6} md={4}>
          <Label className="fw-medium">Filter Lokasi</Label>
          <Input 
            type="select" 
            name="id_lokasi" 
            value={filters.id_lokasi} 
            onChange={handleFilterChange}
            className="form-select"
          >
            <option value="">Semua Lokasi</option>
            {optLokasi.map(v => <option key={v.id_lokasi} value={v.id_lokasi}>{v.nama_lokasi}</option>)}
          </Input>
        </Col>

        {/* Filter Pemilik */}
        <Col xs={12} sm={6} md={4}>
          <Label className="fw-medium">Filter Pemilik</Label>
          <Input 
            type="select" 
            name="id_pemilik" 
            value={filters.id_pemilik} 
            onChange={handleFilterChange}
            className="form-select"
          >
            <option value="">Semua Pemilik</option>
            {optPemilik.map(v => <option key={v.id_pemilik} value={v.id_pemilik}>{v.nama_lengkap}</option>)}
          </Input>
        </Col>

        {/* Group Tombol Aksi */}
        <Col xs={12} md={4} className="d-flex align-items-end">
          <div className="w-100 d-flex gap-2 justify-content-md-end">
            <Button 
              color="light" 
              className="flex-fill flex-md-grow-0" 
              onClick={resetFilter}
              style={{ minWidth: "100px" }}
            >
              <i className="mdi mdi-refresh me-1"></i> Reset
            </Button>
            <Button 
              color="primary" 
              className="flex-fill flex-md-grow-0"
              onClick={() => { setIsEdit(false); setSelectedData(null); setModal(true); }}
            >
              <i className="mdi mdi-plus me-1"></i> Transaksi Baru
            </Button>
          </div>
        </Col>

      </Row>
    </Form>
  </CardBody>
</Card>

          {/* --- TABLE --- */}
          <Card>
            <CardBody>
              <TableContainer columns={columns} data={data} isPagination={false} loading={loading} />
              
              {/* --- PAGINATION --- */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Menampilkan <b>{data.length}</b> dari <b>{pagination.total}</b> data
                </div>
                <Pagination className="pagination-rounded">
                  <PaginationItem disabled={pagination.page <= 1 || loading}>
                    <PaginationLink first onClick={() => fetchData(1)} />
                  </PaginationItem>
                  <PaginationItem disabled={pagination.page <= 1 || loading}>
                    <PaginationLink previous onClick={() => fetchData(pagination.page - 1)} />
                  </PaginationItem>
                  
                  {getPaginationGroup().map((item) => (
                    <PaginationItem active={item === pagination.page} key={item} disabled={loading}>
                      <PaginationLink onClick={() => fetchData(item)}>{item}</PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem disabled={pagination.page >= pagination.last_page || loading}>
                    <PaginationLink next onClick={() => fetchData(pagination.page + 1)} />
                  </PaginationItem>
                  <PaginationItem disabled={pagination.page >= pagination.last_page || loading}>
                    <PaginationLink last onClick={() => fetchData(pagination.last_page)} />
                  </PaginationItem>
                </Pagination>
              </div>
            </CardBody>
          </Card>

          {/* --- MODAL FORM --- */}
          <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" centered>
            <ModalHeader toggle={() => setModal(false)}>{isEdit ? "Edit Transaksi" : "Input Transaksi Baru"}</ModalHeader>
            <ModalBody>
              <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                <Row>
                  <Col md={6} className="mb-3">
                    <Label>Tanggal</Label>
                    <Input type="date" name="tgl_pembelian" {...validation.getFieldProps("tgl_pembelian")} invalid={validation.touched.tgl_pembelian && !!validation.errors.tgl_pembelian} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Lokasi</Label>
                    <Input type="select" name="id_lokasi" {...validation.getFieldProps("id_lokasi")} invalid={validation.touched.id_lokasi && !!validation.errors.id_lokasi}>
                      <option value="">-- Pilih Lokasi --</option>
                      {optLokasi.map(v => <option key={v.id_lokasi} value={v.id_lokasi}>{v.nama_lokasi}</option>)}
                    </Input>
                  </Col>
                  <Col md={12} className="mb-3">
                    <Label>Pemilik Lahan</Label>
                    <Input type="select" name="id_pemilik" {...validation.getFieldProps("id_pemilik")} invalid={validation.touched.id_pemilik && !!validation.errors.id_pemilik}>
                      <option value="">-- Pilih Pemilik --</option>
                      {optPemilik.map(v => <option key={v.id_pemilik} value={v.id_pemilik}>{v.nama_lengkap} ({v.no_ktp})</option>)}
                    </Input>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Luas (m²)</Label>
                    <Input 
                      type="text" 
                      value={formatIDR(validation.values.luas_tanah)} 
                      onChange={(e) => validation.setFieldValue("luas_tanah", parseNumber(e.target.value))} 
                      invalid={validation.touched.luas_tanah && !!validation.errors.luas_tanah}
                    />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Harga Beli (Rp)</Label>
                    <Input 
                      type="text" 
                      value={formatIDR(validation.values.harga_beli_tanah)} 
                      onChange={(e) => validation.setFieldValue("harga_beli_tanah", parseNumber(e.target.value))} 
                      invalid={validation.touched.harga_beli_tanah && !!validation.errors.harga_beli_tanah}
                    />
                  </Col>
                  <Col md={12} className="mb-3">
                  <Label>
                    Upload Hasil Ukur
                    {isEdit && selectedData?.hasil_ukur_path && (
                      <span className="text-muted ms-2 small">(opsional, biarkan kosong jika tidak ingin mengganti)</span>
                    )}
                  </Label>

                  {/* Tampilkan file yang sudah ada saat Edit */}
                  {isEdit && selectedData?.hasil_ukur_path && (
                    <div className="mb-2">
                      <span className="text-muted small">File saat ini: </span>
                      {selectedData.hasil_ukur_path.match(/\.(jpeg|jpg|png|webp|gif)$/i) ? (
                        <img
                          src={selectedData.hasil_ukur_path}
                          alt="Preview"
                          className="img-thumbnail ms-2"
                          style={{ height: "50px", width: "50px", objectFit: "cover" }}
                        />
                      ) : (
                        <a href={selectedData.hasil_ukur_path} target="_blank" rel="noreferrer" className="btn btn-sm btn-soft-danger ms-2">
                          <i className="mdi mdi-file-pdf-box me-1"></i>Lihat File
                        </a>
                      )}
                    </div>
                  )}

                  <Input
                    type="file"
                    name="file_hasil"
                    onChange={(e) => validation.setFieldValue("file_hasil", e.target.files[0] || null)}
                    invalid={validation.touched.file_hasil && !!validation.errors.file_hasil}
                  />
                  {validation.touched.file_hasil && validation.errors.file_hasil && (
                    <FormFeedback>{validation.errors.file_hasil}</FormFeedback>
                  )}
                </Col>
                </Row>
                <div className="text-end mt-3 border-top pt-3">
                  <Button color="light" className="me-2" onClick={() => setModal(false)}>Batal</Button>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Proses..." : "Simpan Transaksi"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          <Modal isOpen={imgModal} toggle={() => setImgModal(false)} centered size="lg">
            <ModalHeader toggle={() => setImgModal(false)}>Pratinjau Dokumen</ModalHeader>
            <ModalBody className="text-center">
              <img src={viewImage} alt="Zoom" className="img-fluid rounded shadow" style={{ maxHeight: '70vh' }} />
            </ModalBody>
          </Modal>

          <ToastContainer />
        </div>
      </div>
    </React.Fragment>
  );
};

export default TransaksiPembelianTanah;